// AdministrarCursosCatalog.jsx - Catálogo de cursos del instructor con grid layout
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { ENDPOINTS as LMS_ENDPOINTS } from '../../../Utils/Endpoints';
import { ENDPOINTS as SECURITY_ENDPOINTS } from '../../../../Seguridad/Utils/Endpoints';
import { cursosNormalize } from '../../../Objetos/CursosModel';
import { Models as LmsModels } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import { getSessionUserId, getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { useLogos } from '../../../../../context/LogoProvider';
import {
    FiBookOpen,
    FiClock,
    FiCheckCircle,
    FiSlash,
    FiUploadCloud,
    FiAlertCircle,
    FiPlusSquare,
    FiSearch,
    FiHelpCircle,
} from 'react-icons/fi';
import AlertDialog from '../../Shared/AlertDialog';

const nombreTabla = "Administrar Cursos";

export default function AdministrarCursosCatalog() {
    const { getSystemConfig } = useLogos();
    const navigate = useNavigate();
    const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
    const COURSE_IMAGE_UPLOAD_ENDPOINT = `${API_BASE}/upload/curso-imagen`;
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [tab, setTab] = useState('todo');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSkill, setSelectedSkill] = useState('');
    const [selectedLevel, setSelectedLevel] = useState('');

    // Estados para el modal de crear curso
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createDraft, setCreateDraft] = useState({
        nombre: "",
        duracion: 1,
        idNivel: "",
        idTipoHabilidad: "",
        idInstructor: null, // Se obtendrá de la sesión
        urlRutaImagen: "",
        autoAsignable: false,
        idEmpresa: getSessionEmpresaId(),
    });
    const [createImagePreview, setCreateImagePreview] = useState(null);
    const [createImageError, setCreateImageError] = useState("");
    const [createImageUploading, setCreateImageUploading] = useState(false);

    // Estado para el diálogo de confirmación de eliminación
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        curso: null,
    });
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setAlertConfig({ open: true, message, variant, title });
    };

    // Datos relacionados
    const [niveles, setNiveles] = useState([]);
    const [instructores, setInstructores] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [tiposHabilidad, setTiposHabilidad] = useState([]);
    const [certificaciones, setCertificaciones] = useState([]);

    // Usuario de sesión
    const [sessionUserId] = useState(() => getSessionUserId());
    const [currentUserEmpleadoId, setCurrentUserEmpleadoId] = useState(null);
    const [isActivatingInstructor, setIsActivatingInstructor] = useState(false);

    // Cargar datos desde BD
    const loadData = async () => {
        setLoading(true);
        try {
            // 1. Obtener ID del usuario de sesión
            const sessionUserId = getSessionUserId();
            const esSuperAdmin = isSessionSuperAdmin();
            const idEmpresa = getSessionEmpresaId();

            if (!sessionUserId) {
                console.warn('[AdministrarCursos] No hay usuario en sesión');
                setRows([]);
                setLoading(false);
                return;
            }

            // OBTENCIÓN OPTIMIZADA DE DATOS
            const [resInstructor, resCursos] = await Promise.all([
                axios.get(LMS_ENDPOINTS.instructorByUsuario(sessionUserId), { withCredentials: true }).catch(e => ({ data: null })),
                axios.get(`${LMS_ENDPOINTS.cursosAdmin}?idEmpresa=${idEmpresa || 0}&esSuperAdmin=${esSuperAdmin}`, { withCredentials: true }).catch(e => ({ data: [] }))
            ]);

            // Procesar Instructor
            let idInstructorActual = null;
            if (resInstructor.data) {
                const instructor = LmsModels.instructor(resInstructor.data);
                idInstructorActual = instructor.idInstructor ?? instructor.IdInstructor;
                console.log('[AdministrarCursos] Instructor actual:', idInstructorActual);

                // Actualizamos el draft con este ID para futuras creaciones
                setCreateDraft(prev => ({ ...prev, idInstructor: idInstructorActual }));
            } else {
                console.warn('[AdministrarCursos] No se pudo obtener datos de instructor para este usuario');
            }

            // Procesar Cursos: filtrar SOLO los del instructor actual.
            // Si no hay perfil de instructor registrado → lista vacía.
            const todosCursos = (resCursos.data || []).map(cursosNormalize);

            const cursosDelInstructor = idInstructorActual
                ? todosCursos.filter(c => String(c.idInstructor) === String(idInstructorActual))
                : [];

            console.log('[AdministrarCursos] Cursos cargados (SP Optimizado):', todosCursos.length, '→ Tuyos:', cursosDelInstructor.length);

            setRows(cursosDelInstructor);
        } catch (error) {
            console.error('Error al cargar cursos:', error);
            // En caso de error, dejar la grilla vacía
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setCreateImageError('Solo se permiten archivos de imagen.');
            return;
        }

        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
        if (file.size > maxMB * 1024 * 1024) {
            setCreateImageError(`La imagen no debe superar los ${maxMB}MB.`);
            return;
        }

        setCreateImageError("");
        const blobUrl = URL.createObjectURL(file);
        setCreateImagePreview(blobUrl);

        try {
            setCreateImageUploading(true);

            // 🔥 BORRAR IMAGEN ANTERIOR DEL S3 SI EXISTE (Draft)
            if (createDraft.urlRutaImagen && createDraft.urlRutaImagen.includes('s3.amazonaws.com')) {
                try {
                    console.log('[AdministrarCursos] 🗑️ Eliminando imagen borrador de S3:', createDraft.urlRutaImagen);
                    await axios.delete(COURSE_IMAGE_UPLOAD_ENDPOINT, {
                        params: { filePath: createDraft.urlRutaImagen },
                        withCredentials: true
                    });
                } catch (deleteError) {
                    console.warn('[AdministrarCursos] ⚠️ Error al eliminar imagen borrador:', deleteError);
                }
            }

            const formData = new FormData();
            formData.append('file', file);

            const resUpload = await axios.post(COURSE_IMAGE_UPLOAD_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true
            });

            const data = resUpload.data;
            if (!data.filePath) {
                throw new Error('La respuesta del servidor no incluye filePath.');
            }

            setCreateDraft(prev => ({
                ...prev,
                urlRutaImagen: data.filePath,
            }));
        } catch (error) {
            console.error('[AdministrarCursos] Error al subir imagen de curso:', error);
            setCreateImageError(error.message || 'Error al subir la imagen al servidor.');
            setCreateImagePreview(null);
            setCreateDraft(prev => ({
                ...prev,
                urlRutaImagen: "",
            }));
        } finally {
            setCreateImageUploading(false);
        }
    };

    // Cargar niveles y tipos de habilidad
    const loadCatalogos = async () => {
        try {
            const [respNiveles, respTiposHabilidad, respCert] = await Promise.all([
                axios.get(LMS_ENDPOINTS.catNivel, { withCredentials: true }),
                axios.get(LMS_ENDPOINTS.catTipoHabilidad, { withCredentials: true }),
                axios.get(LMS_ENDPOINTS.certificaciones, { withCredentials: true }).catch(() => ({ data: [] }))
            ]);
            setNiveles((respNiveles.data || []).map(LmsModels.niveles));
            setTiposHabilidad((respTiposHabilidad.data || []).map(LmsModels.tipoHabilidad));
            setCertificaciones(respCert.data || []);
        } catch (error) {
            console.error('Error al cargar cátalogos:', error);
        }
    };

    // Cargar instructores, empleados y personas
    const loadInstructoresData = async () => {
        try {
            const [resInst, resEmp, resPer] = await Promise.all([
                axios.get(LMS_ENDPOINTS.instructor, { withCredentials: true }),
                axios.get(LMS_ENDPOINTS.empleados, { withCredentials: true }),
                axios.get(LMS_ENDPOINTS.personas, { withCredentials: true })
            ]);
            setInstructores((resInst.data || []).map(LmsModels.instructor));
            setEmpleados((resEmp.data || []).map(LmsModels.empleado));
            setPersonas((resPer.data || []).map(LmsModels.personas));
        } catch (error) {
            console.error('Error al cargar datos de instructores:', error);
        }
    };

    // Obtener instructor desde la sesión del usuario
    const loadInstructorFromSession = async () => {
        if (!sessionUserId) {
            console.warn('[AdministrarCursos] No hay usuario en sesión');
            return;
        }

        try {
            // 1️⃣ Obtener TODOS los usuarios y filtrar por sessionUserId
            const resUser = await axios.get(SECURITY_ENDPOINTS.usuarios, { withCredentials: true });
            const usuarios = resUser.data || [];

            const usuario = usuarios.find(u => {
                const id = u.idUsuario ?? u.IdUsuario ?? u["idUsuario"] ?? u["IdUsuario"];
                return String(id) === String(sessionUserId);
            });

            if (!usuario) {
                console.warn('[AdministrarCursos] Usuario de sesión no encontrado en el catálogo');
                return;
            }

            // 2️⃣ Obtener idPersona del usuario
            const idPersona = usuario.idPersona ?? usuario.IdPersona ?? usuario["idPersona"] ?? usuario["IdPersona"];

            if (!idPersona) {
                console.warn('[AdministrarCursos] Usuario sin persona asociada');
                return;
            }

            // 3️⃣ Buscar empleado por idPersona (ya tenemos empleados cargados)
            const empleado = empleados.find(emp => {
                const empIdPersona = emp.idPersona ?? emp.IdPersona ?? emp["idPersona"] ?? emp["IdPersona"];
                return String(empIdPersona) === String(idPersona);
            });

            if (!empleado) {
                console.warn('[AdministrarCursos] No se encontró empleado asociado a la persona del usuario');
                return;
            }

            // 4️⃣ Obtener idEmpleado
            const idEmpleado = empleado.idEmpleado ?? empleado.IdEmpleado ?? empleado["idEmpleado"] ?? empleado["IdEmpleado"];

            if (!idEmpleado) {
                console.warn('[AdministrarCursos] Empleado sin ID válido');
                return;
            }

            setCurrentUserEmpleadoId(idEmpleado);

            // 5️⃣ Buscar instructor por idEmpleado (ya tenemos instructores cargados)
            const instructor = instructores.find(inst => {
                const instIdEmpleado = inst.idEmpleado ?? inst.IdEmpleado ?? inst["idEmpleado"] ?? inst["IdEmpleado"];
                return String(instIdEmpleado) === String(idEmpleado);
            });

            if (instructor) {
                const idInstructor = instructor.idInstructor ?? instructor.IdInstructor ?? instructor["idInstructor"] ?? instructor["IdInstructor"];
                setCreateDraft(prev => ({
                    ...prev,
                    idInstructor: idInstructor
                }));
                console.log(`✅ Instructor asignado: ${getInstructorNombre(idInstructor)}`);
            } else {
                console.warn('[AdministrarCursos] El empleado no está registrado como instructor');
            }
        } catch (error) {
            console.error('Error al obtener instructor desde sesión:', error);
        }
    };

    const handleInstructorActivation = async () => {
        if (!currentUserEmpleadoId) {
            triggerAlert("No se encontró un registro de empleado para tu usuario de sesión.");
            return;
        }

        setIsActivatingInstructor(true);
        try {
            // Un instructor básico solo necesita el IdEmpleado y Activo = true
            const instructorDTO = {
                IdEmpleado: currentUserEmpleadoId,
                Activo: true
            };

            const response = await axios.post(LMS_ENDPOINTS.instructor, instructorDTO, { withCredentials: true });

            if (response.data) {
                console.log("✅ Perfil de instructor activado satisfactoriamente");
                // Recargar catálogo de instructores y re-detectar sesión
                await loadInstructoresData();
                // Una vez recargado, el useEffect de detección de sesión se encargará de setearlo en el draft
                triggerAlert("Perfil de instructor activado. Ahora puedes crear cursos.", "success", "Éxito");
            }
        } catch (error) {
            console.error("❌ Error al activar perfil de instructor:", error);
            triggerAlert("No se pudo activar tu perfil de instructor en la base de datos.");
        } finally {
            setIsActivatingInstructor(false);
        }
    };

    useEffect(() => {
        loadData();
        loadCatalogos();
        loadInstructoresData();
    }, []);

    // Cargar instructor desde sesión cuando los datos estén disponibles
    useEffect(() => {
        if (instructores.length > 0 && empleados.length > 0 && sessionUserId) {
            loadInstructorFromSession();
        }
    }, [instructores, empleados, sessionUserId]);

    // Filtrado y Ordenamiento
    const filtered = useMemo(() => {
        let data = [...rows];

        // 1. Filtro de tab
        if (tab === 'aprobada') {
            data = data.filter(c => !c.terminado && certificaciones.some(cert => (cert.idCurso || cert.IdCurso) === (c.idCurso || c.id)));
        } else if (tab === 'en-curso') {
            // En curso: cursos SIN certificado Y terminado = false
            data = data.filter(c => !c.terminado && !certificaciones.some(cert => (cert.idCurso || cert.IdCurso) === (c.idCurso || c.id)));
        } else if (tab === 'finalizado') {
            // Finalizado: cursos CON terminado = true
            data = data.filter(c => c.terminado === true);
        }
        // tab === 'todo' muestra todos los cursos

        // 2. Filtro de búsqueda (nombre)
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(c => (c.nombre || '').toLowerCase().includes(term));
        }

        // 3. Filtro por Tipo de Habilidad
        if (selectedSkill) {
            data = data.filter(c => String(c.idTipoHabilidad) === String(selectedSkill));
        }

        // 4. Filtro por Nivel
        if (selectedLevel) {
            data = data.filter(c => String(c.idNivel) === String(selectedLevel));
        }

        // 5. Ordenamiento
        data.sort((a, b) => {
            const getPriority = (curso) => {
                if (curso.terminado) return 3; // Finalizadas al final
                const hasCert = certificaciones.some(cert => (cert.idCurso || cert.IdCurso) === (curso.idCurso || curso.id));
                if (hasCert) return 1; // Aprobadas primero
                return 2; // En curso / publicando
            };
            return getPriority(a) - getPriority(b);
        });

        return data;
    }, [rows, tab, searchTerm, selectedSkill, selectedLevel, certificaciones]);

    // Helper para obtener nombre del instructor
    const getInstructorNombre = (idInstructor) => {
        const inst = instructores.find(i => i.idInstructor === idInstructor);
        if (!inst) return `Instructor ${idInstructor}`;
        const emp = empleados.find(e => e.idEmpleado === inst.idEmpleado);
        if (!emp) return `Instructor ${idInstructor}`;
        const per = personas.find(p => p.idPersona === emp.idPersona);
        return per ? `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim() : `Instructor ${idInstructor}`;
    };

    // Navegar al curso
    const handleVerCurso = (curso) => {
        // DESHABILITADO: Validación de slug - ahora todos los cursos son accesibles
        // if (curso.slug) {
        //     navigate(`/menu/lms/curso/${curso.slug}`);
        // }

        // Navegar usando slug si existe, sino usar ID
        const identifier = curso.slug || curso.id || curso.idCurso;
        navigate(`/menu/lms/curso/${identifier}`);
    };

    // Crear curso
    const handleCrearCurso = () => {
        setCreateModalOpen(true);
    };

    const handleCreateCourseConfirm = async () => {
        const nombre = createDraft.nombre.trim();

        if (!nombre) {
            triggerAlert("Por favor ingresa un nombre para el curso.", "warning", "Atención");
            return;
        }

        if (!createDraft.idNivel) {
            triggerAlert("Por favor selecciona un nivel para el curso.", "warning", "Atención");
            return;
        }

        if (!createDraft.urlRutaImagen) {
            triggerAlert("Por favor selecciona una imagen para el curso.", "warning", "Atención");
            return;
        }

        if (!createDraft.idInstructor) {
            triggerAlert("No se pudo identificar tu perfil de instructor. Por favor contacta a sistemas.");
            return;
        }

        try {
            const cursoDTO = {
                IdInstructor: Number(createDraft.idInstructor),
                Nombre: nombre,
                IdNivel: Number(createDraft.idNivel),
                Duracion: Number(createDraft.duracion) || 0,
                IdContenido: 0,
                IdTipoHabilidad: createDraft.idTipoHabilidad ? Number(createDraft.idTipoHabilidad) : null,
                URLRutaImagen: createDraft.urlRutaImagen || "",
                AutoAsignable: createDraft.autoAsignable,
                Compartido: false, // Por defecto privado para la empresa
                IdEmpresa: createDraft.idEmpresa,
                Activo: true
            };

            const response = await axios.post(LMS_ENDPOINTS.cursos, cursoDTO, { withCredentials: true });

            if (response.data) {
                console.log("✅ Curso creado exitosamente");

                // Recargar cursos
                await loadData();

                // Cerrar modal y limpiar (manteniendo el instructor)
                const currentInstructor = createDraft.idInstructor;
                setCreateModalOpen(false);
                setCreateDraft({
                    nombre: "",
                    duracion: "",
                    idNivel: "",
                    idTipoHabilidad: "",
                    idInstructor: currentInstructor, // Mantener el instructor
                    urlRutaImagen: "",
                    autoAsignable: false
                });
                setCreateImagePreview(null);
                setCreateImageError("");

                // Navegar al currículum del nuevo curso
                const nuevoCursoId = response.data.idCurso || response.data.IdCurso;
                if (nuevoCursoId) {
                    navigate(`/menu/lms/curso/${nuevoCursoId}`);
                }
            }
        } catch (error) {
            console.error("❌ Error al crear curso:", error);
            triggerAlert("No se pudo crear el curso en la base de datos.");
        }
    };


    // Eliminar curso - Abrir diálogo
    const handleEliminarCurso = (curso) => {
        setDeleteDialog({
            open: true,
            curso: curso,
        });
    };

    // Confirmar eliminación
    const confirmDeleteCurso = async () => {
        if (!deleteDialog.curso) return;

        try {
            // 🔥 1. BORRAR IMAGEN DEL CURSO DE S3 (Portada)
            const cursoToDelete = deleteDialog.curso;
            if (cursoToDelete.uRLRutaImagen && cursoToDelete.uRLRutaImagen.includes('s3.amazonaws.com')) {
                try {
                    console.log('[AdministrarCursos] 🗑️ Eliminando imagen de curso de S3:', cursoToDelete.uRLRutaImagen);
                    await axios.delete(COURSE_IMAGE_UPLOAD_ENDPOINT, {
                        params: { filePath: cursoToDelete.uRLRutaImagen },
                        withCredentials: true
                    });
                } catch (deleteError) {
                    console.warn('[AdministrarCursos] ⚠️ Error al eliminar imagen de curso de S3:', deleteError);
                }
            }

            // 🔥 2. BORRAR CONTENIDO DE LECCIONES (Videos e Imágenes en S3)
            // Necesitamos reconstruir la estructura del curso para iterar por sus lecciones
            const cursoId = deleteDialog.curso.idCurso || deleteDialog.curso.id;

            try {
                // Obtener módulos del curso
                const resModulos = await axios.get(`${LMS_ENDPOINTS.modulo}?idCurso=${cursoId}`, { withCredentials: true });
                // La API podría retornar todos los modulos, filtramos
                const allModules = (resModulos.data || []).filter(m => m.idCurso === cursoId);

                if (allModules.length > 0) {
                    console.log(`[Cleaner] Encontrados ${allModules.length} módulos para limpiar.`);

                    // Obtener ModuloContenido para vincular lecciones
                    const resModuloContenido = await axios.get(LMS_ENDPOINTS.moduloContenido, { withCredentials: true });
                    const allModuloContenido = resModuloContenido.data || [];

                    // Obtener TODAS las lecciones (para tener sus datos)
                    // NOTA: Esto podría ser ineficiente si hay miles de lecciones en la BD, 
                    // pero dado que no hay endpoint de "lecciones por curso", es necesario.
                    // Si existiera endpoint leccionesContenido, mejor usar ese para filtrar.
                    const resLeccionesContenido = await axios.get(LMS_ENDPOINTS.leccionesContenido, { withCredentials: true });
                    const allLessonContent = resLeccionesContenido.data || [];

                    // Iteramos módulos -> contenido -> lección -> recursos S3
                    const filesToDelete = [];

                    for (const modulo of allModules) {
                        // IDs de contenidos de este modulo
                        const contentsOfModule = allModuloContenido.filter(mc =>
                            mc.idModulo === modulo.idModulo &&
                            mc.tablaContenido === "Lecciones" &&
                            mc.activo
                        );

                        for (const contentRel of contentsOfModule) {
                            const lessonId = contentRel.idAsociado; // ID de la lección

                            // Buscar el contenido JSON de esta lección
                            const lessonData = allLessonContent.filter(lc => lc.idLeccion === lessonId && lc.activo);

                            lessonData.forEach(item => {
                                try {
                                    const parsedValue = JSON.parse(item.valor);
                                    if (parsedValue.children && Array.isArray(parsedValue.children)) {
                                        parsedValue.children.forEach(col => {
                                            if (col.children && Array.isArray(col.children)) {
                                                col.children.forEach(el => {
                                                    if ((el.type === 'video' || el.type === 'image') &&
                                                        el.src &&
                                                        el.src.includes('s3.amazonaws.com')) {
                                                        filesToDelete.push(el);
                                                    }
                                                });
                                            }
                                        });
                                    }
                                } catch (e) {
                                    // Ignorar errores de parsing
                                }
                            });
                        }
                    }

                    // Eliminar archivos identificados
                    if (filesToDelete.length > 0) {
                        console.log(`[Cleaner] 🗑️ Eliminando ${filesToDelete.length} archivos S3 del curso completo...`);

                        const VIDEO_DELETE_ENDPOINT = `${API_BASE}/upload/lesson-video`;
                        const IMAGE_DELETE_ENDPOINT = `${API_BASE}/upload/lesson-image`;

                        const deletePromises = filesToDelete.map(async (element) => {
                            let deleteEndpoint;
                            if (element.type === 'video') deleteEndpoint = VIDEO_DELETE_ENDPOINT;
                            else if (element.type === 'image') deleteEndpoint = IMAGE_DELETE_ENDPOINT;

                            if (deleteEndpoint) {
                                try {
                                    await axios.delete(deleteEndpoint, {
                                        params: { filePath: element.src },
                                        withCredentials: true
                                    });
                                } catch (error) {
                                    console.warn(`[Cleaner] ⚠️ Error al borrar archivo S3: ${element.src}`, error);
                                }
                            }
                        });
                        await Promise.all(deletePromises);
                    }
                }
            } catch (cleanupError) {
                console.error("❌ Error durante la limpieza de S3 del curso (se continuará con el borrado del registro):", cleanupError);
            }

            // 3. BORRAR EL CURSO DE BASE DE DATOS
            await axios.delete(LMS_ENDPOINTS.cursosById(cursoId), { withCredentials: true });
            console.log("✅ Curso eliminado exitosamente");

            // Cerrar diálogo
            setDeleteDialog({ open: false, curso: null });

            // Recargar cursos
            await loadData();
        } catch (error) {
            console.error("❌ Error al eliminar curso:", error);
            triggerAlert("No se pudo eliminar el curso de la base de datos.");
            // Cerrar diálogo incluso si hay error
            setDeleteDialog({ open: false, curso: null });
        }
    };

    // Helper para renderizar el badge de estado
    const renderStatusPill = (estado) => {
        switch (estado) {
            case 'publicando':
                return (
                    <span className="status-pill publishing">
                        <FiUploadCloud /> Publicando…
                    </span>
                );
            case 'pendiente':
                return (
                    <span className="status-pill pending">
                        <FiAlertCircle /> Pendiente
                    </span>
                );
            case 'en-curso':
                return (
                    <span className="status-pill in-progress">
                        <FiClock /> En curso
                    </span>
                );
            case 'finalizado':
                return (
                    <span className="status-pill completed">
                        <FiCheckCircle /> Finalizado
                    </span>
                );
            case 'aprobado':
                return (
                    <span className="status-pill approved">
                        <FiCheckCircle /> Aprobado
                    </span>
                );
            case 'suspendido':
                return (
                    <span className="status-pill suspended">
                        <FiSlash /> Suspendido
                    </span>
                );
            default:
                return <span className="status-pill">{estado}</span>;
        }
    };

    return (
        <div className="panel-sigma-main">
            <div className="profile-header-title">
                <h1>{nombreTabla}</h1>
            </div>

            {/* Estadísticas Compactas con Línea Primaria */}
            <div className="stats-grid stats-grid-wrap" style={{ marginBottom: '2rem' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiBookOpen size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Total de cursos</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{rows.length}</p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiClock size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>En curso</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                            {rows.filter((x) => !x.terminado).length}
                        </p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiCheckCircle size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Finalizados</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                            {rows.filter((x) => x.terminado === true).length}
                        </p>
                    </div>
                </div>
            </div>

            {/* UI de Toggle, Búsqueda y Filtros - Estilo "Referencia/Comentario" */}
            <div style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))' }}>

                {/* 1. Header (Toggle Tabs) - Mimics .observaciones-reference-header but with Gray Theme */}
                <div style={{
                    display: 'flex',
                    width: '100%',
                    backgroundColor: '#f1f5f9', // Regresando al gris original
                    borderRadius: '12px 12px 0 0',
                    overflow: 'hidden',
                    padding: '0.5rem',
                    gap: '0.5rem',
                    border: '1px solid #e2e8f0',
                    borderBottom: 'none'
                }}>
                    {['todo', 'aprobada', 'en-curso', 'finalizado'].map((option) => (
                        <div
                            key={option}
                            onClick={() => setTab(option)}
                            style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '0.4rem 0',
                                fontSize: '0.9rem',
                                fontWeight: tab === option ? 700 : 500,
                                color: tab === option ? 'var(--color-primary-dark)' : '#64748b', // Texto primario dark active, gris inactive
                                backgroundColor: tab === option ? '#ffffff' : 'transparent', // Fondo blanco active
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                userSelect: 'none',
                                textTransform: 'capitalize',
                                boxShadow: tab === option ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                        >
                            {option.replace('-', ' ')}
                        </div>
                    ))}
                </div>

                {/* 2. Body (Search & Filters) - Mimics .observaciones-bubble but White */}
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '0.8rem 1rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    {/* Búsqueda */}
                    <div style={{
                        flex: 1, // Ocupar todo el espacio restante disponible
                        minWidth: '200px', // Mínimo razonable para evitar colapso total
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '999px',
                        padding: '0.4rem 1rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                        <FiSearch style={{ color: 'var(--color-primary-dark)', marginRight: '0.6rem', fontSize: '1rem' }} />
                        <input
                            type="text"
                            placeholder="Buscar cursos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.9rem', color: '#1e293b' }}
                        />
                    </div>

                    {/* Filtros */}
                    <div style={{ display: 'flex', gap: '0.8rem', flex: '0 0 auto', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <select
                            value={selectedSkill}
                            onChange={(e) => setSelectedSkill(e.target.value)}
                            style={{
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                padding: '0.4rem 2rem 0.4rem 1rem',
                                fontSize: '0.85rem',
                                color: '#475569',
                                outline: 'none',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                minWidth: '160px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        >
                            <option value="">Habilidad</option>
                            {tiposHabilidad.map(th => (
                                <option key={th.idTipoHabilidad} value={th.idTipoHabilidad}>{th.nombre}</option>
                            ))}
                        </select>

                        <select
                            value={selectedLevel}
                            onChange={(e) => setSelectedLevel(e.target.value)}
                            style={{
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                padding: '0.4rem 2rem 0.4rem 1rem',
                                fontSize: '0.85rem',
                                color: '#475569',
                                outline: 'none',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                minWidth: '140px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        >
                            <option value="">Nivel</option>
                            {niveles.map(n => (
                                <option key={n.idNivel} value={n.idNivel}>{n.nombre}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Grid de cursos */}
            {loading ? (
                <div className="catalog-loading">Cargando cursos...</div>
            ) : tab === "publicando" ? (
                <div className="courses-grid fixed-cards">
                    {/* Tarjeta fantasma para crear nuevo curso (Publicando) */}
                    <article
                        className="course-card fixed-card phantom-create-card"
                        onClick={handleCrearCurso}
                        style={{
                            border: '2px dashed #cbd5e1',
                            backgroundColor: '#f8fafc',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '380px',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: 'none',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="phantom-icon-wrapper" style={{
                            backgroundColor: 'var(--color-primary-100)',
                            color: 'var(--color-primary-dark)',
                            width: '72px',
                            height: '72px',
                            borderRadius: '24px',
                            border: '2px solid var(--color-primary-dark)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '2.5rem',
                            marginBottom: '1.5rem',
                            transition: 'all 0.3s ease'
                        }}>
                            <FiPlusSquare />
                        </div>
                        <div style={{ textAlign: 'center', padding: '0 1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.3rem', color: '#334155', fontWeight: 700 }}>Crear nuevo curso</h3>
                            <p style={{ margin: '0.6rem 0 0', fontSize: '0.95rem', color: '#64748b', lineHeight: 1.4 }}>Comienza a diseñar tu contenido educativo ahora mismo</p>
                        </div>
                    </article>

                    {filtered.map((curso) => {
                        const key = curso.idCurso || curso.id;
                        const isAprobado = certificaciones.some(cert => (cert.idCurso || cert.IdCurso) === key);
                        const statusVisuals = (() => {
                            if (curso.terminado) {
                                return { bg: '#15803d', icon: <FiCheckCircle /> }; // Verde
                            } else if (isAprobado) {
                                return { bg: '#0369a1', icon: <FiCheckCircle /> }; // Azul
                            } else {
                                return { bg: '#ca8a04', icon: <FiUploadCloud /> }; // Amarillo
                            }
                        })();

                        return (
                            <article key={key} className="course-card fixed-card" style={{ border: `1px solid ${statusVisuals.bg}`, transition: 'all 0.3s ease', overflow: 'hidden' }}>
                                <div className={`course-thumb course-thumb-${curso.thumb || 1}`} style={{ position: 'relative' }}>
                                    {/* Triángulo de estatus en la esquina superior derecha */}
                                    <div style={{
                                        position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', zIndex: 5, overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                                            borderStyle: 'solid', borderWidth: '0 60px 60px 0',
                                            borderColor: `transparent ${statusVisuals.bg} transparent transparent`,
                                            filter: 'drop-shadow(-1px 1px 2px rgba(0,0,0,0.2))'
                                        }} />
                                        <div style={{
                                            position: 'absolute', top: '8px', right: '8px', color: 'white',
                                            fontSize: '1.5rem', display: 'flex', zIndex: 6
                                        }}>
                                            {statusVisuals.icon}
                                        </div>
                                    </div>
                                </div>
                                <div className="course-body" style={{
                                    backgroundColor: curso.terminado
                                        ? '#f0fdf4'
                                        : isAprobado
                                            ? '#eff6ff'
                                            : '#fffbeb',
                                    flex: 1,
                                    transition: 'background-color 0.3s ease'
                                }}>
                                    <h3 className="course-title">{curso.nombre}</h3>
                                    <p className="course-meta">
                                        {certificaciones.some(cert => (cert.idCurso || cert.IdCurso) === (curso.idCurso || curso.id)) ? (
                                            <span className="status-pill approved" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                                                <FiCheckCircle /> Aprobado
                                            </span>
                                        ) : (
                                            <span className="status-pill publishing">
                                                <FiUploadCloud /> Publicando…
                                            </span>
                                        )}
                                    </p>
                                    <div className="progress-track" style={{ marginTop: '1rem' }}>
                                        <div
                                            className="progress-bar"
                                            style={{ width: `${curso.progreso || 28}%` }}
                                        ></div>
                                    </div>
                                    <p className="progress-label">
                                        Progreso de publicación: {curso.progreso || 28}%
                                    </p>
                                    <div className="course-actions" style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
                                        {!curso.terminado && (
                                            <button
                                                type="button"
                                                className="btn-cancel"
                                                onClick={() => handleEliminarCurso(curso)}
                                                style={{
                                                    flexShrink: 0,
                                                    padding: '0.4rem 0.6rem', // Ajuste para icono más grande
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: `1px solid ${statusVisuals.bg}`,
                                                    backgroundColor: isAprobado ? '#eff6ff' : '#fffbeb'
                                                }}
                                                title="Eliminar curso"
                                            >
                                                <img src={sigmaDelete} alt="" style={{ width: '24px', height: '24px' }} />
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            className="btn-action"
                                            style={{
                                                flex: 1,
                                                fontSize: '13px',
                                                padding: '0.4rem 1rem',
                                                cursor: 'pointer',
                                                transition: '0.2s',
                                                fontFamily: "'Outfit', sans-serif",
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            onClick={() => handleVerCurso(curso)}
                                        >
                                            Ver curso
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            ) : (
                <div className="courses-grid fixed-cards">
                    {/* Tarjeta fantasma para crear nuevo curso */}
                    {!searchTerm && tab === 'todo' && (
                        <article
                            className="course-card fixed-card phantom-create-card"
                            onClick={handleCrearCurso}
                            style={{
                                border: '2px dashed #cbd5e1',
                                backgroundColor: '#f8fafc',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '380px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: 'none',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <div className="phantom-icon-wrapper" style={{
                                backgroundColor: 'var(--color-primary-100)',
                                color: 'var(--color-primary-dark)',
                                width: '72px',
                                height: '72px',
                                borderRadius: '24px',
                                border: '2px solid var(--color-primary-dark)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '2.5rem',
                                marginBottom: '1.5rem',
                                transition: 'all 0.3s ease'
                            }}>
                                <FiPlusSquare />
                            </div>
                            <div style={{ textAlign: 'center', padding: '0 1rem' }}>
                                <h3 style={{ margin: 0, fontSize: '1.3rem', color: '#334155', fontWeight: 700 }}>Crear nuevo curso</h3>
                                <p style={{ margin: '0.6rem 0 0', fontSize: '0.95rem', color: '#64748b', lineHeight: 1.4 }}>Comienza a diseñar tu contenido educativo ahora mismo</p>
                            </div>
                        </article>
                    )}

                    {filtered.length === 0 ? (
                        filtered.length === 0 && searchTerm ? (
                            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                                No se encontraron cursos.
                            </div>
                        ) : null
                    ) : (
                        filtered.map((curso) => {
                            const key = curso.idCurso || curso.id;
                            const isAprobado = certificaciones.some(cert => (cert.idCurso || cert.IdCurso) === key);
                            const statusVisuals = (() => {
                                if (curso.terminado) {
                                    return { bg: '#15803d', icon: <FiCheckCircle /> }; // Verde
                                } else if (isAprobado) {
                                    return { bg: '#0369a1', icon: <FiCheckCircle /> }; // Azul
                                } else {
                                    return { bg: '#ca8a04', icon: <FiClock /> }; // Amarillo
                                }
                            })();

                            return (
                                <article key={key} className="course-card fixed-card" style={{ border: `1px solid ${statusVisuals.bg}`, transition: 'all 0.3s ease', overflow: 'hidden' }}>
                                    {curso.uRLRutaImagen ? (
                                        <div className="course-thumb" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
                                            <img
                                                src={curso.uRLRutaImagen.startsWith('http') ? curso.uRLRutaImagen : `${SERVER_URL}${curso.uRLRutaImagen}`}
                                                alt={curso.nombre || 'Curso'}
                                                style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
                                            />
                                            {/* Triángulo de estatus en la esquina superior derecha */}
                                            <div style={{
                                                position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', zIndex: 5, overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                                                    borderStyle: 'solid', borderWidth: '0 60px 60px 0',
                                                    borderColor: `transparent ${statusVisuals.bg} transparent transparent`,
                                                    filter: 'drop-shadow(-1px 1px 2px rgba(0,0,0,0.2))'
                                                }} />
                                                <div style={{
                                                    position: 'absolute', top: '8px', right: '8px', color: 'white',
                                                    fontSize: '1.5rem', display: 'flex', zIndex: 6
                                                }}>
                                                    {statusVisuals.icon}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`course-thumb course-thumb-${curso.thumb || 1}`} style={{ position: 'relative' }}>
                                            {/* Triángulo de estatus en la esquina superior derecha */}
                                            <div style={{
                                                position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', zIndex: 5, overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                                                    borderStyle: 'solid', borderWidth: '0 60px 60px 0',
                                                    borderColor: `transparent ${statusVisuals.bg} transparent transparent`,
                                                    filter: 'drop-shadow(-1px 1px 2px rgba(0,0,0,0.2))'
                                                }} />
                                                <div style={{
                                                    position: 'absolute', top: '8px', right: '8px', color: 'white',
                                                    fontSize: '1.5rem', display: 'flex', zIndex: 6
                                                }}>
                                                    {statusVisuals.icon}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="course-body" style={{
                                        backgroundColor: curso.terminado
                                            ? '#f0fdf4'
                                            : isAprobado
                                                ? '#eff6ff'
                                                : '#fffbeb',
                                        flex: 1,
                                        transition: 'background-color 0.3s ease'
                                    }}>
                                        <h3 className="course-title">{curso.nombre}</h3>
                                        {/* Mostrar Habilidad si existe */}
                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                            {curso.idTipoHabilidad && tiposHabilidad.length > 0 && (
                                                <span style={{
                                                    fontSize: '0.7rem',
                                                    backgroundColor: '#e0f2fe',
                                                    color: '#0369a1',
                                                    padding: '2px 8px',
                                                    borderRadius: '9999px',
                                                }}>
                                                    {tiposHabilidad.find(th => th.idTipoHabilidad === curso.idTipoHabilidad)?.nombre || "Habilidad"}
                                                </span>
                                            )}
                                            {curso.compartido && (
                                                <span style={{
                                                    fontSize: '0.7rem',
                                                    backgroundColor: '#fef3c7',
                                                    color: '#92400e',
                                                    padding: '2px 8px',
                                                    borderRadius: '9999px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '3px'
                                                }}>
                                                    🤝 Compartido
                                                </span>
                                            )}
                                        </div>
                                        <p className="course-meta">
                                            {curso.terminado ? (
                                                <span className="status-pill completed">
                                                    <FiCheckCircle /> Finalizado
                                                </span>
                                            ) : certificaciones.some(cert => (cert.idCurso || cert.IdCurso) === (curso.idCurso || curso.id)) ? (
                                                <span className="status-pill approved" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                                                    <FiCheckCircle /> Aprobado
                                                </span>
                                            ) : (
                                                <span className="status-pill in-progress">
                                                    <FiClock /> En Curso
                                                </span>
                                            )}
                                        </p>

                                        {curso.estado === "pendiente" && (
                                            <p className="pending-line">
                                                <FiAlertCircle /> En espera de publicación
                                            </p>
                                        )}
                                        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>
                                            <div>⏱️ {curso.duracion}</div>
                                        </div>
                                        <div className="course-actions" style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
                                            {!curso.terminado && (
                                                <button
                                                    type="button"
                                                    className="btn-cancel"
                                                    style={{
                                                        flexShrink: 0,
                                                        padding: '0.5rem 0.7rem', // Ajuste para icono más grande
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: `1px solid ${statusVisuals.bg}`,
                                                        backgroundColor: isAprobado ? '#eff6ff' : '#fffbeb'
                                                    }}
                                                    onClick={() => handleEliminarCurso(curso)}
                                                    title="Eliminar curso"
                                                >
                                                    <img src={sigmaDelete} alt="" style={{ width: '24px', height: '24px' }} />
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                className="btn-action"
                                                style={{
                                                    flex: 1,
                                                    fontSize: '13px',
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    transition: '0.2s',
                                                    fontFamily: "'Outfit', sans-serif",
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                                onClick={() => handleVerCurso(curso)}
                                            >
                                                Ver curso
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            );
                        })
                    )}
                </div>
            )}

            {/* MODAL CREAR CURSO */}
            {createModalOpen && (
                <div className="cc-course-modal-overlay">
                    <div className="cc-course-modal">
                        <h3 className="cc-course-modal-title">Crear curso</h3>
                        <p className="cc-course-modal-subtitle">
                            Ajusta la información inicial del curso. Podrás modificar estos
                            datos más adelante desde el currículum.
                        </p>

                        <div className="cc-course-modal-body">
                            <div className="cc-course-modal-field">
                                <label>Nombre del curso</label>
                                <input
                                    type="text"
                                    value={createDraft.nombre}
                                    onChange={(e) =>
                                        setCreateDraft((prev) => ({
                                            ...prev,
                                            nombre: e.target.value,
                                        }))
                                    }
                                    placeholder="Ej. Seguridad Industrial Básica"
                                />
                            </div>

                            <div className="cc-course-modal-grid">
                                <div className="cc-course-modal-field">
                                    <label>Nivel</label>
                                    <select
                                        value={createDraft.idNivel}
                                        onChange={(e) =>
                                            setCreateDraft((prev) => ({
                                                ...prev,
                                                idNivel: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">Selecciona un nivel</option>
                                        {niveles.map((nivel) => (
                                            <option key={nivel.idNivel} value={nivel.idNivel}>
                                                {nivel.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="cc-course-modal-field">
                                    <label>Tipo de Habilidad</label>
                                    <select
                                        value={createDraft.idTipoHabilidad || ""}
                                        onChange={(e) =>
                                            setCreateDraft((prev) => ({
                                                ...prev,
                                                idTipoHabilidad: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">Selecciona una Habilidad</option>
                                        {tiposHabilidad.map((th) => (
                                            <option key={th.idTipoHabilidad} value={th.idTipoHabilidad}>
                                                {th.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* SELECTOR DE INSTRUCTOR (Solo para Administradores) */}
                            {isSessionSuperAdmin() ? (
                                <div className="cc-course-modal-field">
                                    <label>Instructor Responsable</label>
                                    {!createDraft.idInstructor ? (
                                        <div style={{
                                            padding: '1rem',
                                            backgroundColor: '#fff7ed',
                                            border: '1px solid #ffedd5',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.8rem'
                                        }}>
                                            <p style={{ fontSize: '0.85rem', color: '#9a3412', margin: 0 }}>
                                                No tienes un perfil de instructor activo. Debes activarlo para ser el responsable de este curso.
                                            </p>
                                            <button
                                                type="button"
                                                className="btn-primary"
                                                onClick={handleInstructorActivation}
                                                disabled={isActivatingInstructor || !currentUserEmpleadoId}
                                                style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                                            >
                                                {isActivatingInstructor ? 'Activando...' : 'Activar mi perfil de Instructor'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{
                                            padding: '0.8rem 1rem',
                                            backgroundColor: '#f0fdf4',
                                            border: '1px solid #dcfce7',
                                            borderRadius: '8px',
                                            fontSize: '0.9rem',
                                            color: '#166534',
                                            fontWeight: 600
                                        }}>
                                            👤 Instructor: {getInstructorNombre(createDraft.idInstructor)}
                                        </div>
                                    )}
                                </div>
                            ) : null}

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 14px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '10px',
                                border: '1px solid #e2e8f0',
                                marginBottom: '1rem'
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <label htmlFor="autoAsignableCreate" style={{
                                        marginBottom: 0,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        color: '#1e293b'
                                    }}>
                                        Libre inscripción
                                        <FiHelpCircle
                                            style={{ color: '#64748b', fontSize: '0.9rem' }}
                                            title="Si se activa, los alumnos podrán inscribirse por sí mismos. Si no, solo RH podrá asignarlo."
                                        />
                                    </label>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Permite la auto-inscripción de alumnos.</span>
                                </div>
                                <div
                                    onClick={() => setCreateDraft(prev => ({ ...prev, autoAsignable: !prev.autoAsignable }))}
                                    style={{
                                        width: '44px',
                                        height: '22px',
                                        backgroundColor: createDraft.autoAsignable ? '#10b981' : '#cbd5e1',
                                        borderRadius: '11px',
                                        position: 'relative',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '3px',
                                        left: createDraft.autoAsignable ? '25px' : '3px',
                                        transition: 'all 0.3s'
                                    }} />
                                </div>
                            </div>





                            <div className="cc-course-modal-field">
                                <label>Imagen del curso</label>
                                {/* Área de carga con preview integrado */}
                                <div
                                    style={{
                                        border: createDraft.urlRutaImagen
                                            ? '2px solid #10b981'
                                            : `2px dashed ${createImageError ? '#ef4444' : '#cbd5e1'}`,
                                        borderRadius: 8,
                                        padding: '0.75rem',
                                        backgroundColor: createDraft.urlRutaImagen ? '#ecfdf5' : '#f9fafb',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                    }}
                                >
                                    {/* Badge de estado */}
                                    {createDraft.urlRutaImagen && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '12px',
                                                right: '12px',
                                                backgroundColor: '#10b981',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                zIndex: 10,
                                            }}
                                        >
                                            <span>✓</span>
                                            <span>Archivo cargado</span>
                                        </div>
                                    )}

                                    {/* Vista previa si existe */}
                                    {createDraft.urlRutaImagen && (
                                        <div style={{ marginBottom: '1rem' }}>
                                            <p style={{
                                                fontSize: '0.85rem',
                                                color: '#64748b',
                                                marginBottom: '0.8rem',
                                                fontWeight: 600
                                            }}>
                                                Vista previa:
                                            </p>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <img
                                                    src={createImagePreview || `${SERVER_URL}${createDraft.urlRutaImagen}`}
                                                    alt="Vista previa del curso"
                                                    style={{
                                                        width: '100%',
                                                        maxHeight: 180,
                                                        borderRadius: 6,
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Botón de carga */}
                                    <label
                                        htmlFor="cc-course-image-input"
                                        style={{
                                            cursor: createImageUploading ? 'wait' : 'pointer',
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '0.25rem',
                                            padding: createDraft.urlRutaImagen ? '0.5rem' : '0',
                                            opacity: createImageUploading ? 0.6 : 1,
                                        }}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleCreateImageChange}
                                            style={{ display: 'none' }}
                                            id="cc-course-image-input"
                                            disabled={createImageUploading}
                                        />
                                        <div style={{ fontSize: '1.3rem', color: createDraft.urlRutaImagen ? '#059669' : '#64748b' }}>
                                            {createImageUploading ? '⏳' : createDraft.urlRutaImagen ? '🔄' : '☁️'}
                                        </div>
                                        <div style={{ color: createDraft.urlRutaImagen ? '#059669' : '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                                            {createImageUploading ? 'Subiendo imagen...' : createDraft.urlRutaImagen ? 'Cambiar imagen' : 'Haz clic para subir imagen'}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: createDraft.urlRutaImagen ? '#059669' : '#94a3b8' }}>
                                            JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
                                        </div>
                                    </label>
                                </div>

                                {createImageError && (
                                    <small style={{ color: '#b91c1c', fontSize: '.8rem', display: 'block', marginTop: 4 }}>
                                        {createImageError}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="cc-course-modal-actions">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => setCreateModalOpen(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="cc-btn-primary"
                                onClick={handleCreateCourseConfirm}
                                disabled={createImageUploading}
                                style={{ opacity: createImageUploading ? 0.7 : 1, cursor: createImageUploading ? 'not-allowed' : 'pointer' }}
                            >
                                {createImageUploading ? 'Subiendo imagen...' : 'Crear curso'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DIÁLOGO DE CONFIRMACIÓN DE ELIMINACIÓN */}
            <ConfirmDeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, curso: null })}
                onConfirm={confirmDeleteCurso}
                roleName={deleteDialog.curso?.nombre || 'este curso'}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
